import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ScenarioMachineEntry } from '@/types/flexe';
import { MachineSpecInput } from './MachineSpecInput';
import { Trash2, ArrowRight } from 'lucide-react';
import { computeKW } from '@/lib/flexeKWConversion';

interface ScenarioMachineEntryCardProps {
  entry: ScenarioMachineEntry;
  index: number;
  onChange: (entry: ScenarioMachineEntry) => void;
  onRemove: () => void;
}

export function ScenarioMachineEntryCard({
  entry,
  index,
  onChange,
  onRemove,
}: ScenarioMachineEntryCardProps) {
  // Calculate delta
  let delta: number | null = null;
  try {
    if (entry.type === 'new') {
      delta = computeKW(entry.new_machine);
    } else if (entry.type === 'replacement' && entry.old_machine) {
      delta = computeKW(entry.new_machine) - computeKW(entry.old_machine);
    }
  } catch (e) {
    // Invalid specs
  }

  return (
    <Card className="p-4 space-y-3">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Badge variant="outline">{index + 1}</Badge>
          {/* Type Dropdown */}
          <Select
            value={entry.type}
            onValueChange={(type: 'new' | 'replacement') =>
              onChange({ ...entry, type, old_machine: type === 'new' ? undefined : {} })
            }
          >
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="new">Nieuwe machine</SelectItem>
              <SelectItem value="replacement">Vervanging</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button variant="ghost" size="sm" onClick={onRemove}>
          <Trash2 className="w-4 h-4 text-destructive" />
        </Button>
      </div>

      {/* Machine Specs */}
      {entry.type === 'new' ? (
        <MachineSpecInput
          label="Machine specs"
          spec={entry.new_machine}
          onChange={(spec) => onChange({ ...entry, new_machine: spec })}
        />
      ) : (
        <div className="space-y-3">
          <MachineSpecInput
            label="Oude machine"
            spec={entry.old_machine || {}}
            onChange={(spec) => onChange({ ...entry, old_machine: spec })}
          />
          <MachineSpecInput
            label="Nieuwe machine"
            spec={entry.new_machine}
            onChange={(spec) => onChange({ ...entry, new_machine: spec })}
          />

          {/* Delta Badge */}
          {delta !== null && (
            <div className="flex items-center gap-2 text-sm p-2 bg-muted rounded">
              <ArrowRight className="w-4 h-4" />
              <span className={delta >= 0 ? 'text-green-600 font-medium' : 'text-red-600 font-medium'}>
                Delta: {delta >= 0 ? '+' : ''}{delta.toFixed(1)} kW
              </span>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}
